import { MenuManager } from "../menus/menu-manager";
import { GameState } from "../state/game-state";
import { WsConnection } from "../utilities/ws-connection";
import { ControllerContextKey } from "./contexts/contextKeys";

type GameContext = {
  gameState: GameState;
  menuManager: MenuManager;
  controller: Controller;
  wsConnection?: WsConnection;
};

type ButtonHandler = {
  onDown: (e: PointerEvent | KeyboardEvent, gameContext: GameContext) => void;
  onUp: (e: PointerEvent | KeyboardEvent, gameContext: GameContext) => void;
};

export type Buttons = {
  up: HTMLButtonElement;
  down: HTMLButtonElement;
  left: HTMLButtonElement;
  right: HTMLButtonElement;
  actionA: HTMLButtonElement;
  actionB: HTMLButtonElement;
  select: HTMLButtonElement;
  start: HTMLButtonElement;
};

const buttonKeyMap: Record<keyof Buttons, string[]> = {
  up: ["ArrowUp", "w"],
  down: ["ArrowDown", "s"],
  left: ["ArrowLeft", "a"],
  right: ["ArrowRight", "d"],
  actionA: ["KeyA", "e"],
  actionB: ["KeyB", "f"],
  select: ["Enter"],
  start: ["q"],
};

export type ControllerContext = Partial<
  Record<keyof Buttons, Partial<ButtonHandler>>
>;

export class Controller {
  currentContext: ControllerContextKey[] = [];
  activePointers: Set<number> = new Set();
  activeKeys: Set<string> = new Set();
  gameContext: GameContext;

  constructor(
    gameContext: Omit<GameContext, "controller">,
    private buttons: Buttons,
    private controllerContexts: Record<ControllerContextKey, ControllerContext>,
  ) {
    this.gameContext = { ...gameContext, controller: this };
    this.registerHandlers(this.buttons);
  }

  pushContext(context: ControllerContextKey) {
    this.currentContext.push(context);
  }

  replaceContext(context: ControllerContextKey) {
    this.currentContext[Math.max(this.currentContext.length - 1, 0)] = context;
  }

  popContext() {
    this.currentContext.pop();
  }

  private getContextHandlerFor(
    button: keyof Buttons,
  ): Partial<ButtonHandler> | undefined {
    const current = this.currentContext.at(-1);
    return this.controllerContexts[current as ControllerContextKey]?.[button];
  }

  private registerHandlers(buttons: Buttons) {
    for (const [button, element] of Object.entries(buttons)) {
      window.addEventListener("keydown", (e) => {
        if (buttonKeyMap[button as keyof Buttons].some((k) => k === e.key)) {
          this.activeKeys.add(button);
          const handler = this.getContextHandlerFor(button as keyof Buttons);
          handler?.onDown?.(e, this.gameContext);
        }
      });
      window.addEventListener("keyup", (e) => {
        if (buttonKeyMap[button as keyof Buttons].some((k) => k === e.key)) {
          this.activeKeys.delete(button);
          const handler = this.getContextHandlerFor(button as keyof Buttons);
          handler?.onUp?.(e, this.gameContext);
        }
      });
      element.addEventListener("contextmenu", function (e) {
        e.preventDefault();
        e.stopPropagation();
      });
      element.addEventListener(
        "pointerdown",
        (e) => {
          e.preventDefault();
          console.log("down", e);
          this.activePointers.add(e.pointerId);
          const handler = this.getContextHandlerFor(button as keyof Buttons);
          handler?.onDown?.(e, this.gameContext);
        },
        { passive: false },
      );
      element.addEventListener("pointerup", (e) => {
        if (!this.activePointers.has(e.pointerId)) return;
        this.activePointers.delete(e.pointerId);
        console.log("up", e);
        const handler = this.getContextHandlerFor(button as keyof Buttons);
        handler?.onUp?.(e, this.gameContext);
      });
      element.addEventListener("pointerleave", (e) => {
        if (!this.activePointers.has(e.pointerId)) return;
        this.activePointers.delete(e.pointerId);
        console.log("leave", e);
        const handler = this.getContextHandlerFor(button as keyof Buttons);
        handler?.onUp?.(e, this.gameContext);
      });
      element.addEventListener("lostpointercapture", (e) => {
        if (!this.activePointers.has(e.pointerId)) return;
        this.activePointers.delete(e.pointerId);
        console.log("lost", e);
        const handler = this.getContextHandlerFor(button as keyof Buttons);
        handler?.onUp?.(e, this.gameContext);
      });
    }
  }
}
